import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Copy, Check, Clock, ShieldCheck, Share2, PlayCircle } from "lucide-react";
import { toast } from "sonner";

export default function VouchesPending() {
  const { user, refresh } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [copiedTok, setCopiedTok] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/vouches/mine");
      setData(data);
      await refresh();
    } catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, []);

  const copy = (token) => {
    const link = `${window.location.origin}/vouch/${token}`;
    navigator.clipboard.writeText(link);
    setCopiedTok(token);
    toast.success("Vouch link copied — send it to them.");
    setTimeout(() => setCopiedTok(""), 2000);
  };

  if (!data) return <div className="min-h-screen flex items-center justify-center paper text-ink/50">Loading…</div>;
  const licensed = data.license_status === "licensed";
  const provisional = data.license_status === "provisional";

  return (
    <div className="min-h-screen paper py-10 px-6">
      <div className="max-w-3xl mx-auto">
        <Link to="/" data-testid="vouches-logo"><Logo /></Link>

        <div className="card-paper p-10 mt-8">
          <div className="eyebrow">Your license status</div>
          <div className="flex items-center justify-between mt-2">
            <div>
              <h1 className="font-display text-4xl text-ink" style={{ fontWeight: 400 }}>
                {licensed ? <>Welcome, <em className="italic text-ivy">{user?.name?.split(" ")[0]}.</em></> :
                 provisional ? <>You're <em className="italic text-ivy">provisionally licensed.</em></> :
                 <>Awaiting your <em className="italic text-ivy">first vouch.</em></>}
              </h1>
              <p className="mt-2 text-ink/70">
                {licensed && "Three vouches confirmed. Your License to Love is issued."}
                {provisional && `${data.vouches_confirmed} of 3 confirmed. Reach three within 14 days for full license.`}
                {data.license_status === "unlicensed" && "Send the vouch links below. One confirmed vouch and you're in."}
              </p>
            </div>
            <div className="text-right">
              <div className="text-xs text-ochre-300 font-mono">{data.license_no}</div>
              <div className="text-xs text-ink/50 font-mono mt-1">
                {data.vouches_confirmed}/{data.vouches_required} vouched
              </div>
            </div>
          </div>

          {licensed && (
            <div className="mt-8 relative rounded-2xl p-10 shadow-lg paper" style={{ border: "2px double #D4A574" }}>
              <div className="text-center ornament eyebrow text-ivy-500">Certificate of Character</div>
              <h3 className="text-center font-display italic mt-3 text-ink" style={{ fontFamily: "Fraunces", fontSize: 40, fontWeight: 400 }}>License to Love</h3>
              <div className="text-center mt-4 text-ink/70">
                Certified for <span className="italic font-display text-ivy" style={{ fontFamily: "Fraunces", fontSize: 22 }}>{user?.name}</span>
              </div>
              <div className="flex items-center justify-between mt-8">
                <div className="text-xs text-ink/60 font-mono">Issued · {new Date().toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" })}</div>
                <div className="seal animate-seal-in">Character<br/><em>Verified</em></div>
                <div className="text-xs text-ink/60 font-mono">Registrar of Kindness</div>
              </div>
              <div className="mt-6 flex justify-center gap-3">
                <Link to="/onboarding" data-testid="v-enter-app" className="btn-pill bg-ivy text-parchment hover:bg-ivy-600">Enter Wholesome →</Link>
                <Link to={`/license/${data.license_no}`} data-testid="v-share-license" className="btn-pill bg-ink text-parchment hover:bg-ivy flex items-center gap-2">
                  <Share2 size={14} /> Share my License
                </Link>
              </div>
            </div>
          )}

          {provisional && (
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link to="/onboarding" data-testid="v-enter-provisional" className="btn-pill bg-ivy text-parchment hover:bg-ivy-600">Enter with provisional license →</Link>
              <Link to={`/license/${data.license_no}`} data-testid="v-share-license-prov" className="btn-pill bg-white border border-ink/10 text-ink hover:bg-parchment-50 flex items-center gap-2">
                <Share2 size={14} /> Share my License
              </Link>
              <span className="text-xs text-ochre-300 font-mono"><Clock size={12} className="inline mr-1" />14-day window</span>
            </div>
          )}

          <div className="mt-10">
            <div className="eyebrow">Your vouchers</div>
            <div className="mt-4 space-y-3">
              {data.invites.map((inv) => {
                const submitted = inv.status === "submitted";
                return (
                  <div key={inv.id} className="p-4 rounded-xl border border-ink/10 bg-white flex items-center gap-4" data-testid={`invite-${inv.id}`}>
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center ${submitted ? "bg-ivy text-parchment" : "bg-fog text-ink/60"}`}>
                      {submitted ? <Check size={16} /> : <Clock size={16} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-ink">{inv.invitee_name}<span className="text-xs text-ink/50 ml-2">· {inv.relationship}</span></div>
                      <div className="text-xs text-ink/50 mt-0.5">
                        {submitted ? "Vouched ✓" : `Waiting for their vouch · sent ${new Date(inv.created_at).toLocaleDateString()}`}
                      </div>
                    </div>
                    {!submitted && (
                      <button data-testid={`copy-${inv.token}`} onClick={() => copy(inv.token)}
                        className="btn-pill text-xs bg-ink text-parchment hover:bg-ivy flex items-center gap-1.5">
                        {copiedTok === inv.token ? <><Check size={12}/> Copied</> : <><Copy size={12}/> Copy link</>}
                      </button>
                    )}
                  </div>
                );
              })}
              {data.invites.length === 0 && <div className="text-sm text-ink/50">No vouchers added yet.</div>}
            </div>
          </div>

          {data.vouches.length > 0 && (
            <div className="mt-10">
              <div className="eyebrow">What they said about you</div>
              <div className="mt-4 space-y-4">
                {data.vouches.map((v) => (
                  <div key={v.id} className="p-6 rounded-xl bg-parchment-50 border border-amber-100" data-testid={`vouch-${v.id}`}>
                    <div className="flex items-center justify-between text-xs text-ochre-300 font-mono">
                      <div className="flex items-center gap-2">
                        <ShieldCheck size={12} /> {v.voucher_name.toUpperCase()} · {v.relationship.toUpperCase()} · {v.how_long_known.replace(/_/g, " ").toUpperCase()}
                      </div>
                      {v.video_url && <span className="flex items-center gap-1 text-ivy"><PlayCircle size={12}/> VIDEO</span>}
                    </div>
                    {v.video_url && (
                      <video src={`${process.env.REACT_APP_BACKEND_URL}${v.video_url}`} controls
                        className="mt-3 w-full max-w-md rounded-xl aspect-square object-cover bg-ink" />
                    )}
                    <blockquote className="mt-3 font-display italic text-ink text-lg leading-relaxed" style={{ fontFamily: "Fraunces" }}>
                      "{v.kind_story}"
                    </blockquote>
                  </div>
                ))}
              </div>
            </div>
          )}

          {err && <div className="text-sm text-ochre-300 mt-4">{err}</div>}
        </div>
      </div>
    </div>
  );
}
